import React, { useMemo, useState } from "react";

import { isAxiosError } from "axios";
import { useParams } from "react-router-dom";

import useDeleteGoalBasedFinancialPlanInsurance from "@fartherfinance/frontend/api/FinancialPlans/insurance/hooks/useDeleteGoalBasedFinancialPlanInsurance";
import usePatchGoalBasedFinancialPlanInsurance from "@fartherfinance/frontend/api/FinancialPlans/insurance/hooks/usePatchGoalBasedFinancialPlanInsurance";
import usePostGoalBasedFinancialPlanInsurance from "@fartherfinance/frontend/api/FinancialPlans/insurance/hooks/usePostGoalBasedFinancialPlanInsurance";
import {
  GoalBasedFinancialPlanInsuranceResponse,
  GoalBasedInsuranceType,
  GoalBasedInsuranceTypeSchema,
} from "@fartherfinance/frontend/api/FinancialPlans/insurance/types";
import { GoalBasedFinancialPlanSingleResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { OwnerFieldType } from "../Shared/Types";
import InsuranceCardListRow from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Insurance/InsuranceCardListRow/InsuranceCardListRow";
import CardsRow from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/CardsRow/CardsRow";
import {
  ClientAndSpouseDetails,
  ClientDetails,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/hooks/useGetClientAndSpouseDetails";
import type { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import MenuOptionsWithCheckIfSelected from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import { InsuranceOption, insuranceOptions } from "./constants";
import DisabilityFormDrawer from "./Forms/Disability";
import LifeFormDrawer from "./Forms/Life";
import LongTermCareFormDrawer from "./Forms/LongTermCare";
import PropertyAndCasualityInsuranceForm from "./Forms/PropertyAndCasuality";

import styles from "./Insurance.module.css";

interface Props {
  singleGoalBasedFinancialPlan: GoalBasedFinancialPlanSingleResponse;
  ownerOptions: OwnerFieldType[];
  clientSpouseData: ClientAndSpouseDetails;
}

const FinancialPlanInsurance: React.FC<Props> = ({
  singleGoalBasedFinancialPlan,
  ownerOptions,
  clientSpouseData,
}) => {
  const { clientId } = useParams<{
    clientId: ClientId;
  }>();
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [selectedOwner, setSelectedOwner] = useState<ClientDetails | undefined>(
    undefined
  );

  const [drawer, setDrawer] = useState<GoalBasedInsuranceType | undefined>(
    undefined
  );

  const [selectedInsurance, setSelectedInsurance] = useState<
    GoalBasedFinancialPlanInsuranceResponse | undefined
  >();

  const auth = useRequestAuth();

  const statusNotification = useStatusNotification();

  const { client, spouse } = clientSpouseData;

  const { fullName: clientFullName } = client;

  const {
    mutateAsync: postGoalBasedFinancialPlanInsurance,
    isLoading: isPostingGoalBasedFinancialPlanGoal,
  } = usePostGoalBasedFinancialPlanInsurance(auth);

  const {
    mutateAsync: deleteGoalBasedFinancialPlanInsurance,
    isLoading: isDeletingGoalBasedFinancialPlanGoal,
  } = useDeleteGoalBasedFinancialPlanInsurance(auth);

  const {
    mutateAsync: patchGoalBasedFinancialPlanInsurance,
    isLoading: isMutatingGoalBasedFinancialPlanGoal,
  } = usePatchGoalBasedFinancialPlanInsurance(auth);

  const handleOnCloseDrawer = () => {
    setDrawer(undefined);
  };

  const handleOnSelectEmptyOption =
    ({
      owner,
      insuranceType,
    }: {
      owner: ClientDetails | undefined;
      insuranceType: GoalBasedInsuranceType;
    }) =>
    () => {
      setSelectedOwner(owner);
      setSelectedInsurance(undefined);
      setDrawer(insuranceType);
      setShowDropdown(false);
    };

  const handleOnSelectInsuranceOptionById = ({ id }: InsuranceOption) => {
    setSelectedInsurance(undefined);
    setDrawer(id);
    setShowDropdown(false);
  };

  const handleOnSelectEditInsurance = (
    insurance: GoalBasedFinancialPlanInsuranceResponse
  ) => {
    const owner = insurance.owner !== clientId ? spouse : client;

    setSelectedOwner(owner);
    setSelectedInsurance(insurance);
    setDrawer(insurance.insuranceType);
    setShowDropdown(false);
  };

  const getCardHeading = (
    insuranceType: GoalBasedInsuranceType | undefined
  ) => {
    if (client.firstName === undefined) {
      return "";
    }

    const insuranceTypeLabel = insuranceOptions.find(
      (t) => t.id === insuranceType
    )?.label;

    if (insuranceTypeLabel === undefined) {
      return "";
    }

    if (insuranceType === GoalBasedInsuranceTypeSchema.Enum.LongTerm) {
      return "Long-Term Care Insurance";
    }

    return `${insuranceTypeLabel} Insurance`;
  };

  const handleOnRemove = async () => {
    if (selectedInsurance === undefined) {
      throw new Error("Goal to remove not found");
    }

    if (deleteGoalBasedFinancialPlanInsurance === undefined) {
      throw new Error("deleteGoalBasedFinancialPlanGoal not found");
    }
    try {
      await deleteGoalBasedFinancialPlanInsurance({
        goalBasedFinancialPlanId:
          singleGoalBasedFinancialPlan.goalBasedFinancialPlanId,
        goalBasedFinancialPlanInsuranceId: selectedInsurance.planInsuranceId,
      });

      statusNotification("Insurance removed", "Success");

      setDrawer(undefined);
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        statusNotification(
          `Error removing insurance: ${message ?? ""}`,
          "Error"
        );
      } else {
        statusNotification(
          "There was an error removing the insurance. Please try again.",
          "Error"
        );
      }
    }
  };

  const { insurance } = singleGoalBasedFinancialPlan;

  const defaultOwner = useMemo(() => {
    const emptyOwner = ownerOptions.find(
      (o) => o.value === selectedOwner?.clientId
    );
    return emptyOwner ? emptyOwner : { label: clientFullName, value: clientId };
  }, [selectedOwner, clientId, clientFullName, ownerOptions]);

  const commonDrawerProps = {
    clientId,
    goalBasedFinancialPlanId:
      singleGoalBasedFinancialPlan.goalBasedFinancialPlanId,
    selectedInsurance,
    defaultOwner,
    ownerOptions: ownerOptions,
    retirementHorizons: singleGoalBasedFinancialPlan.retirementHorizon,
    onClose: handleOnCloseDrawer,
    onRemove: handleOnRemove,
    postGoalBasedFinancialPlanInsurance,
    patchGoalBasedFinancialPlanInsurance,
    isLoading:
      isMutatingGoalBasedFinancialPlanGoal ||
      isDeletingGoalBasedFinancialPlanGoal ||
      isPostingGoalBasedFinancialPlanGoal,
  };

  const [clientInsurance, spouseInsurance] = useMemo(() => {
    const clientInsurance = insurance.filter((i) => i.owner === clientId);
    const spouseInsurance = insurance.filter(
      (i) => i.owner === spouse?.clientId
    );
    return [clientInsurance, spouseInsurance];
  }, [insurance, clientId, spouse]);

  const getClientInsuranceByType = (insuranceType: GoalBasedInsuranceType) => {
    return clientInsurance.filter((el) => el.insuranceType === insuranceType);
  };

  const getSpouseInsuranceByType = (insuranceType: GoalBasedInsuranceType) => {
    return spouseInsurance.filter((el) => el.insuranceType === insuranceType);
  };

  const { Disability, Life, LongTerm, PropertyAndCasualty } =
    GoalBasedInsuranceTypeSchema.Enum;

  return (
    <>
      <PageHero
        className={styles.pageHero}
        innerClassName={styles.pageHeroInner}
        start={
          <TitleBlock
            title="Insurance"
            subTitle="Add details about your current insurance coverage."
          />
        }
        end={
          <CustomDropdownBase
            className={styles.dropdownBase}
            curItem={{
              value: "Add Insurance",
              displayComponent: (
                <Text variant={"body"} color={"text"}>
                  Add Insurance
                </Text>
              ),
            }}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            width={240}
            isDisabled={false}
            hideIcon={true}
          >
            <MenuOptionsWithCheckIfSelected
              onSelect={handleOnSelectInsuranceOptionById}
              curItemName={"Add Insurance"}
              options={insuranceOptions}
            />
          </CustomDropdownBase>
        }
        endClassName={styles.pageHeroEnd}
      />
      <BodyContentFormatter>
        <div className={styles.heading}>
          <Text variant={"heading1"} color={"text"}>
            Insurance Policies
          </Text>

          <Text variant={"body"} color={"textSubtle"}>
            {pluralize(insurance.length, "Policy", true)}
          </Text>
        </div>

        <CardsRow maxColumns={2}>
          {[Disability, Life, LongTerm, PropertyAndCasualty].map((type) => (
            <React.Fragment key={type}>
              <InsuranceCardListRow
                insuranceList={getClientInsuranceByType(type)}
                insuranceType={type}
                fullName={clientFullName}
                heading={getCardHeading(type)}
                onAddEmptyInsurance={handleOnSelectEmptyOption({
                  owner: client,
                  insuranceType: type,
                })}
                onClick={handleOnSelectEditInsurance}
              />

              <>
                {singleGoalBasedFinancialPlan.retirementHorizon.some(
                  (rh) => rh.owner !== clientId
                ) && (
                  <InsuranceCardListRow
                    insuranceList={getSpouseInsuranceByType(type)}
                    insuranceType={type}
                    fullName={spouse?.fullName ?? "Spouse"}
                    heading={getCardHeading(type)}
                    onAddEmptyInsurance={handleOnSelectEmptyOption({
                      owner: spouse,
                      insuranceType: type,
                    })}
                    onClick={handleOnSelectEditInsurance}
                  />
                )}
              </>
            </React.Fragment>
          ))}
        </CardsRow>

        <LifeFormDrawer drawerOpen={drawer === Life} {...commonDrawerProps} />

        <DisabilityFormDrawer
          drawerOpen={drawer === Disability}
          {...commonDrawerProps}
        />

        <LongTermCareFormDrawer
          drawerOpen={drawer === LongTerm}
          {...commonDrawerProps}
        />

        <PropertyAndCasualityInsuranceForm
          drawerOpen={drawer === PropertyAndCasualty}
          {...commonDrawerProps}
        />
      </BodyContentFormatter>
    </>
  );
};

export default FinancialPlanInsurance;
